import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchContacts, fetchPipelineStages, fetchCategories, createContact, updateContact, exportContacts, importContacts, batchUpdateStage, batchDeleteContacts, createInteraction } from '../api/crm';
import type { Contact, PipelineStage, Category } from '../types/crm';
import { toDateOnly, formatDateDDMMYYYY } from '../utils/date';

type ViewMode = 'list' | 'board' | 'overview';
type InteractionType = 'CALL' | 'EMAIL' | 'MEETING' | 'MESSAGE' | 'OTHER';

interface MultiSelectProps {
  label: string;
  options: { value: string; label: string }[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  isOpen: boolean;
  onToggleOpen: () => void;
}

function MultiSelectDropdown({ label, options, selected, onChange, isOpen, onToggleOpen }: MultiSelectProps) {
  const count = selected.size;
  const display = count === 0 ? label : `${label} (${count})`;
  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={onToggleOpen}
        style={{
          padding: '6px 12px',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius)',
          background: count > 0 ? 'var(--color-warm-bg)' : 'white',
          fontSize: '13px',
          fontWeight: count > 0 ? 600 : 400,
          cursor: 'pointer',
        }}
      >
        {display} ▾
      </button>
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 50,
            background: 'white',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: '8px',
            minWidth: '180px',
            maxHeight: '260px',
            overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          {options.length === 0 && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', padding: '4px' }}>No options</div>
          )}
          {options.map((opt) => (
            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px', fontSize: '13px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selected.has(opt.value)}
                onChange={() => {
                  const next = new Set(selected);
                  if (next.has(opt.value)) next.delete(opt.value); else next.add(opt.value);
                  onChange(next);
                }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

const INACTIVE_DAYS = 100;

function isOverdue(c: Contact): boolean {
  if (!c.next_follow_up_date) return false;
  return new Date(c.next_follow_up_date) < new Date();
}

function isInactive(c: Contact): boolean {
  if (!c.last_contacted_at) return true;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - INACTIVE_DAYS);
  return new Date(c.last_contacted_at) < cutoff;
}

export default function CrmKanbanPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null);
  const [pendingDrop, setPendingDrop] = useState<{ contactId: string; stageId: string } | null>(null);
  const [pendingDropFollowUp, setPendingDropFollowUp] = useState('');
  const [importMsg, setImportMsg] = useState('');
  const [importError, setImportError] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkStageId, setBulkStageId] = useState('');
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');
  const [bulkError, setBulkError] = useState(false);
  const [inlineStagePending, setInlineStagePending] = useState<{ contactId: string; stageId: string } | null>(null);
  const [inlineStageFollowUp, setInlineStageFollowUp] = useState('');
  const [stageFilter, setStageFilter] = useState<Set<string>>(new Set());
  const [ownerFilter, setOwnerFilter] = useState<Set<string>>(new Set());
  const [outreachFilter, setOutreachFilter] = useState<Set<string>>(new Set());
  const [genderFilter, setGenderFilter] = useState<Set<string>>(new Set());
  const [activeOnly, setActiveOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [openFilter, setOpenFilter] = useState<'stage' | 'owner' | 'outreach' | 'gender' | null>(null);
  const [logContactId, setLogContactId] = useState<string | null>(null);
  const [logType, setLogType] = useState<InteractionType>('CALL');
  const [logNotes, setLogNotes] = useState('');
  const [logSaving, setLogSaving] = useState(false);
  const [logStep2, setLogStep2] = useState(false);
  const [logFollowUp, setLogFollowUp] = useState('');
  const [archiveSelectedIds, setArchiveSelectedIds] = useState<Set<string>>(new Set());
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    outreach_type: 'SALES' as const,
    stage_id: '',
    next_follow_up_date: '',
    job_title: '',
    gender: '',
    employment_status: '',
  });

  const load = useCallback(() => {
    Promise.all([fetchContacts(false), fetchPipelineStages(), fetchCategories()])
      .then(([c, s, cat]) => {
        setContacts(c);
        setStages(s);
        setCategories(cat);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const byStage = stages.reduce<Record<string, Contact[]>>((acc, s) => {
    acc[s.id] = contacts.filter((c) => c.stage_id === s.id);
    return acc;
  }, {});

  const categoryMap = categories.reduce<Record<string, Category>>((acc, c) => {
    acc[c.id] = c;
    return acc;
  }, {});

  const stageOrder = useMemo(
    () => new Map(stages.map((s) => [s.id, s.order_index])),
    [stages]
  );

  const stageById = useMemo(
    () => new Map(stages.map((s) => [s.id, s])),
    [stages]
  );

  const sortedContacts = useMemo(() => {
    const copy = contacts.slice();
    copy.sort((a, b) => {
      const oa = stageOrder.get(a.stage_id) ?? -Infinity;
      const ob = stageOrder.get(b.stage_id) ?? -Infinity;
      if (oa !== ob) return ob - oa;
      if (!a.next_follow_up_date && !b.next_follow_up_date) return 0;
      if (!a.next_follow_up_date) return 1;
      if (!b.next_follow_up_date) return -1;
      return a.next_follow_up_date.localeCompare(b.next_follow_up_date);
    });
    return copy;
  }, [contacts, stageOrder]);

  const ownerOptions = useMemo(() => {
    const s = new Set<string>();
    contacts.forEach((c) => { if (c.owner) s.add(c.owner); });
    return Array.from(s).sort();
  }, [contacts]);

  const filteredContacts = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return sortedContacts.filter((c) => {
      if (stageFilter.size > 0 && !stageFilter.has(c.stage_id)) return false;
      if (ownerFilter.size > 0 && !(c.owner && ownerFilter.has(c.owner))) return false;
      if (outreachFilter.size > 0 && !outreachFilter.has(c.outreach_type)) return false;
      if (genderFilter.size > 0 && !(c.gender && genderFilter.has(c.gender))) return false;
      if (activeOnly && c.employment_status === 'changed') return false;
      if (overdueOnly) {
        if (!c.next_follow_up_date || c.next_follow_up_date >= today) return false;
      }
      return true;
    });
  }, [sortedContacts, stageFilter, ownerFilter, outreachFilter, genderFilter, activeOnly, overdueOnly]);

  const hasActiveFilter =
    stageFilter.size > 0 ||
    ownerFilter.size > 0 ||
    outreachFilter.size > 0 ||
    genderFilter.size > 0 ||
    activeOnly ||
    overdueOnly;

  const todayStr = new Date().toISOString().slice(0, 10);
  const totalActive = contacts.length;
  const dueToday = contacts.filter((c) => c.next_follow_up_date === todayStr).length;
  const overdueCount = contacts.filter((c) => c.next_follow_up_date && c.next_follow_up_date < todayStr).length;
  const proposalStage = stages.find((s) => s.name === 'Proposal Sent');
  const inProposal = proposalStage ? contacts.filter((c) => c.stage_id === proposalStage.id).length : 0;

  const stageCounts = useMemo(
    () => stages.map((s) => ({ stage: s, count: contacts.filter((c) => c.stage_id === s.id).length })),
    [stages, contacts]
  );
  const maxStageCount = Math.max(1, ...stageCounts.map((x) => x.count));

  const actionRequired = useMemo(() => {
    return contacts
      .filter((c) => c.next_follow_up_date && c.next_follow_up_date < todayStr)
      .sort((a, b) => (a.next_follow_up_date ?? '').localeCompare(b.next_follow_up_date ?? ''));
  }, [contacts, todayStr]);

  const hotLeads = useMemo(() => {
    const hotStageIds = new Set(
      stages.filter((s) => s.name === 'Engaged' || s.name === 'Proposal Sent').map((s) => s.id)
    );
    return contacts
      .filter((c) => hotStageIds.has(c.stage_id))
      .sort((a, b) => {
        if (!a.next_follow_up_date && !b.next_follow_up_date) return 0;
        if (!a.next_follow_up_date) return 1;
        if (!b.next_follow_up_date) return -1;
        return a.next_follow_up_date.localeCompare(b.next_follow_up_date);
      });
  }, [contacts, stages]);

  const archiveCandidates = useMemo(() => {
    const cutoff = Date.now() - 90 * 86400000;
    const wonStageId = stages.find((s) => s.name === 'Closed Won')?.id;
    return contacts
      .filter((c) => c.stage_id !== wonStageId)
      .filter((c) => !c.last_contacted_at || new Date(c.last_contacted_at).getTime() < cutoff)
      .sort((a, b) => {
        if (!a.last_contacted_at && !b.last_contacted_at) return 0;
        if (!a.last_contacted_at) return -1;
        if (!b.last_contacted_at) return 1;
        return a.last_contacted_at.localeCompare(b.last_contacted_at);
      });
  }, [contacts, stages]);

  const selectedStage = stages.find((s) => s.id === form.stage_id);
  const stageRequiresFollowUp = selectedStage?.requires_follow_up ?? false;

  const handleCreateContact = useCallback(async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.stage_id) {
      setError('First name, last name and stage are required');
      return;
    }
    if (stageRequiresFollowUp && !form.next_follow_up_date.trim()) {
      setError('Next follow-up date is required for this stage');
      return;
    }
    try {
      const { id } = await createContact({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        company: form.company.trim() || undefined,
        email: form.email.trim() || undefined,
        outreach_type: form.outreach_type,
        stage_id: form.stage_id,
        ...(form.next_follow_up_date.trim() ? { next_follow_up_date: form.next_follow_up_date.trim() } : {}),
        job_title: form.job_title.trim() || undefined,
        gender: form.gender || undefined,
        employment_status: form.employment_status || undefined,
      });
      setDrawerOpen(false);
      setForm({
        first_name: '',
        last_name: '',
        company: '',
        email: '',
        outreach_type: 'SALES',
        stage_id: stages[0]?.id ?? '',
        next_follow_up_date: '',
        job_title: '',
        gender: '',
        employment_status: '',
      });
      load();
      navigate(`/crm/contact/${id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create contact');
    }
  }, [form, stages, stageRequiresFollowUp, load, navigate]);

  const handleDrop = useCallback(async (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    setDragOverStageId(null);
    const contactId = e.dataTransfer.getData('contactId');
    if (!contactId) return;
    const contact = contacts.find((c) => c.id === contactId);
    if (!contact || contact.stage_id === stageId) return;
    const stage = stages.find((s) => s.id === stageId);
    if (stage?.requires_follow_up) {
      setPendingDrop({ contactId, stageId });
      setPendingDropFollowUp(new Date().toISOString().slice(0, 10));
      return;
    }
    setContacts((prev) => prev.map((c) => c.id === contactId ? { ...c, stage_id: stageId } : c));
    try {
      await updateContact(contactId, { stage_id: stageId });
    } catch {
      load();
    }
  }, [contacts, stages, load]);

  const handlePendingDropConfirm = useCallback(async () => {
    if (!pendingDrop || !pendingDropFollowUp) return;
    const { contactId, stageId } = pendingDrop;
    setContacts((prev) => prev.map((c) => c.id === contactId ? { ...c, stage_id: stageId, next_follow_up_date: pendingDropFollowUp } : c));
    setPendingDrop(null);
    try {
      await updateContact(contactId, { stage_id: stageId, next_follow_up_date: pendingDropFollowUp });
    } catch {
      load();
    }
  }, [pendingDrop, pendingDropFollowUp, load]);

  const handleFileSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportMsg('Importing…');
    setImportError(false);
    try {
      const r = await importContacts(file);
      const base = `${r.imported} imported, ${r.updated} updated`;
      if (r.errors.length) {
        setImportError(true);
        setImportMsg(`${base}; ${r.errors.length} error(s): ${r.errors.map((x) => `row ${x.row}: ${x.message}`).join('; ')}`);
      } else {
        setImportError(false);
        setImportMsg(base);
      }
      load();
    } catch (err) {
      setImportError(true);
      setImportMsg(err instanceof Error ? err.message : 'Import failed');
    }
  }, [load]);

  const toggleOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const ids = filteredContacts.map((c) => c.id);
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [filteredContacts]);

  const applyBulk = useCallback(async () => {
    if (!bulkStageId || selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const stageName = stages.find((s) => s.id === bulkStageId)?.name ?? '';
    setBulkApplying(true);
    setBulkMsg('');
    setBulkError(false);
    try {
      const { updated } = await batchUpdateStage(ids, bulkStageId);
      setBulkMsg(`${updated} contacts moved to ${stageName}`);
      setBulkError(false);
      setSelectedIds(new Set());
      setBulkStageId('');
      setBulkConfirmOpen(false);
      load();
    } catch (e) {
      setBulkError(true);
      setBulkMsg(e instanceof Error ? e.message : 'Bulk update failed');
    } finally {
      setBulkApplying(false);
    }
  }, [bulkStageId, selectedIds, stages, load]);

  const applyDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      await batchDeleteContacts(Array.from(selectedIds));
      setSelectedIds(new Set());
      setDeleteConfirmOpen(false);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete contacts');
    } finally {
      setDeleting(false);
    }
  }, [selectedIds, load]);

  const handleInlineStageChange = useCallback(async (contact: Contact, stageId: string) => {
    if (contact.stage_id === stageId) return;
    const stage = stages.find((s) => s.id === stageId);
    if (stage?.requires_follow_up && !contact.next_follow_up_date) {
      setInlineStagePending({ contactId: contact.id, stageId });
      setInlineStageFollowUp(new Date().toISOString().slice(0, 10));
      return;
    }
    setContacts((prev) => prev.map((x) => x.id === contact.id ? { ...x, stage_id: stageId } : x));
    try {
      await updateContact(contact.id, { stage_id: stageId });
    } catch {
      load();
    }
  }, [stages, load]);

  const confirmInlineStage = useCallback(async () => {
    if (!inlineStagePending || !inlineStageFollowUp) return;
    const { contactId, stageId } = inlineStagePending;
    setContacts((prev) => prev.map((c) => c.id === contactId ? { ...c, stage_id: stageId, next_follow_up_date: inlineStageFollowUp } : c));
    setInlineStagePending(null);
    try {
      await updateContact(contactId, { stage_id: stageId, next_follow_up_date: inlineStageFollowUp });
    } catch {
      load();
    }
  }, [inlineStagePending, inlineStageFollowUp, load]);

  const saveLogContact = useCallback(async () => {
    if (!logContactId) return;
    setLogSaving(true);
    try {
      await createInteraction({ contact_id: logContactId, type: logType, notes: logNotes.trim() || undefined });
      setLogFollowUp(new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10));
      setLogStep2(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to log contact');
    } finally {
      setLogSaving(false);
    }
  }, [logContactId, logType, logNotes]);

  const closeLogModal = useCallback(() => {
    setLogContactId(null);
    setLogNotes('');
    setLogStep2(false);
    setLogFollowUp('');
  }, []);

  const setLogFollowUpDate = useCallback(async () => {
    if (!logContactId || !logFollowUp) return;
    setLogSaving(true);
    try {
      await updateContact(logContactId, { next_follow_up_date: logFollowUp });
      closeLogModal();
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to set follow-up date');
    } finally {
      setLogSaving(false);
    }
  }, [logContactId, logFollowUp, closeLogModal, load]);

  const skipLogFollowUp = useCallback(() => {
    closeLogModal();
    load();
  }, [closeLogModal, load]);

  const toggleArchiveOne = useCallback((id: string) => {
    setArchiveSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleArchiveAll = useCallback(() => {
    setArchiveSelectedIds((prev) => {
      const ids = archiveCandidates.map((c) => c.id);
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(ids);
    });
  }, [archiveCandidates]);

  const applyArchive = useCallback(async () => {
    const ids = Array.from(archiveSelectedIds);
    if (ids.length === 0) return;
    setArchiving(true);
    try {
      const now = new Date().toISOString();
      await Promise.all(ids.map((id) => updateContact(id, { archived_at: now })));
      setArchiveSelectedIds(new Set());
      setArchiveConfirmOpen(false);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to archive contacts');
    } finally {
      setArchiving(false);
    }
  }, [archiveSelectedIds, load]);

  const clearAllFilters = useCallback(() => {
    setStageFilter(new Set());
    setOwnerFilter(new Set());
    setOutreachFilter(new Set());
    setGenderFilter(new Set());
    setActiveOnly(false);
    setOverdueOnly(false);
  }, []);

  const handleBarClick = useCallback((stageId: string) => {
    setStageFilter(new Set([stageId]));
    setOwnerFilter(new Set());
    setOutreachFilter(new Set());
    setGenderFilter(new Set());
    setActiveOnly(false);
    setOverdueOnly(false);
    setViewMode('list');
  }, []);

  useEffect(() => {
    if (!openFilter) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || !target.closest('[data-filter-dropdown]')) {
        setOpenFilter(null);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [openFilter]);

  if (loading) return <div>Loading…</div>;
  if (error) return <div style={{ color: 'var(--color-coral)' }}>{error}</div>;

  return (
    <>
      <p style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-blue-dark)', fontWeight: 600, marginBottom: '8px' }}>CRM</p>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '36px', fontWeight: 700, margin: 0 }}>Pipeline</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                background: viewMode === 'list' ? 'var(--color-coral)' : 'white',
                color: viewMode === 'list' ? 'white' : 'var(--color-text-primary)',
                fontWeight: 500,
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              List
            </button>
            <button
              type="button"
              onClick={() => setViewMode('board')}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                background: viewMode === 'board' ? 'var(--color-coral)' : 'white',
                color: viewMode === 'board' ? 'white' : 'var(--color-text-primary)',
                fontWeight: 500,
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Board
            </button>
            <button
              type="button"
              onClick={() => setViewMode('overview')}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                background: viewMode === 'overview' ? 'var(--color-coral)' : 'white',
                color: viewMode === 'overview' ? 'white' : 'var(--color-text-primary)',
                fontWeight: 500,
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Overview
            </button>
          </div>
          <button
            type="button"
            onClick={exportContacts}
            style={{ padding: '10px 16px', background: 'white', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', fontWeight: 500, cursor: 'pointer', fontSize: '14px' }}
          >
            Export CSV
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            style={{ padding: '10px 16px', background: 'white', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', fontWeight: 500, cursor: 'pointer', fontSize: '14px' }}
          >
            Import CSV
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleFileSelected}
          />
          <button
            type="button"
            onClick={() => {
              const defaultStageId = stages[0]?.id ?? '';
              const defaultStage = stages.find((s) => s.id === defaultStageId);
              const defaultNextFollowUp = defaultStage?.requires_follow_up
                ? new Date().toISOString().slice(0, 10)
                : '';
              setForm({ ...form, stage_id: defaultStageId, next_follow_up_date: defaultNextFollowUp });
              setDrawerOpen(true);
            }}
            className="btn btn-primary"
          >
            New contact
          </button>
        </div>
      </div>

      {importMsg && (
        <div
          style={{
            marginBottom: '16px',
            padding: '10px 14px',
            border: `1px solid ${importError ? 'var(--color-coral)' : 'var(--color-border)'}`,
            background: 'white',
            borderRadius: 'var(--radius)',
            fontSize: '13px',
            color: importError ? 'var(--color-coral)' : 'var(--color-text-muted)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span>{importMsg}</span>
          <button
            type="button"
            onClick={() => setImportMsg('')}
            style={{ background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '16px' }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {viewMode === 'list' && (
        <div data-filter-dropdown style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <MultiSelectDropdown
            label="Stage"
            options={stages.map((s) => ({ value: s.id, label: s.name }))}
            selected={stageFilter}
            onChange={setStageFilter}
            isOpen={openFilter === 'stage'}
            onToggleOpen={() => setOpenFilter(openFilter === 'stage' ? null : 'stage')}
          />
          <MultiSelectDropdown
            label="Owner"
            options={ownerOptions.map((o) => ({ value: o, label: o }))}
            selected={ownerFilter}
            onChange={setOwnerFilter}
            isOpen={openFilter === 'owner'}
            onToggleOpen={() => setOpenFilter(openFilter === 'owner' ? null : 'owner')}
          />
          <MultiSelectDropdown
            label="Outreach"
            options={[
              { value: 'SALES', label: 'Sales' },
              { value: 'PARTNERSHIP', label: 'Partnership' },
              { value: 'INVESTOR', label: 'Investor' },
              { value: 'OTHER', label: 'Other' },
            ]}
            selected={outreachFilter}
            onChange={setOutreachFilter}
            isOpen={openFilter === 'outreach'}
            onToggleOpen={() => setOpenFilter(openFilter === 'outreach' ? null : 'outreach')}
          />
          <MultiSelectDropdown
            label="Gender"
            options={[
              { value: 'M', label: 'M' },
              { value: 'F', label: 'F' },
              { value: 'O', label: 'O' },
            ]}
            selected={genderFilter}
            onChange={setGenderFilter}
            isOpen={openFilter === 'gender'}
            onToggleOpen={() => setOpenFilter(openFilter === 'gender' ? null : 'gender')}
          />
          <button
            type="button"
            onClick={() => setActiveOnly((v) => !v)}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              background: activeOnly ? 'var(--color-blue-accent)' : 'white',
              color: activeOnly ? 'white' : 'var(--color-text-primary)',
              fontSize: '13px',
              fontWeight: activeOnly ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            Active only
          </button>
          <button
            type="button"
            onClick={() => setOverdueOnly((v) => !v)}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius)',
              background: overdueOnly ? 'var(--color-coral)' : 'white',
              color: overdueOnly ? 'white' : 'var(--color-text-primary)',
              fontSize: '13px',
              fontWeight: overdueOnly ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            Overdue only
          </button>
          {hasActiveFilter && (
            <button
              type="button"
              onClick={clearAllFilters}
              style={{ background: 'transparent', border: 'none', color: 'var(--color-blue-dark)', textDecoration: 'underline', cursor: 'pointer', fontSize: '13px' }}
            >
              Clear all filters
            </button>
          )}
        </div>
      )}

      {viewMode === 'list' && selectedIds.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
          padding: '10px 14px', marginBottom: '16px',
          border: '1px solid var(--color-border)', borderRadius: 'var(--radius)',
          background: 'var(--color-warm-bg)', fontSize: '14px',
        }}>
          <span style={{ fontWeight: 600 }}>{selectedIds.size} contacts selected</span>
          <select
            value={bulkStageId}
            onChange={(e) => setBulkStageId(e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', background: 'white' }}
          >
            <option value="">— Select stage —</option>
            {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button
            type="button"
            disabled={!bulkStageId || bulkApplying}
            onClick={() => setBulkConfirmOpen(true)}
            className="btn btn-primary btn-sm"
          >Apply</button>
          <button
            type="button"
            onClick={() => setDeleteConfirmOpen(true)}
            style={{
              padding: '8px 16px', background: 'var(--color-coral)', color: 'white',
              border: 'none', borderRadius: 'var(--radius)', fontWeight: 600,
              cursor: 'pointer',
            }}
          >Delete selected</button>
          <button
            type="button"
            onClick={() => { setSelectedIds(new Set()); setBulkStageId(''); }}
            style={{ background: 'transparent', border: 'none', color: 'var(--color-blue-dark)', cursor: 'pointer', textDecoration: 'underline', fontSize: '13px' }}
          >Clear selection</button>
          {bulkMsg && (
            <span style={{ color: bulkError ? 'var(--color-coral)' : 'var(--color-text-muted)', fontSize: '13px' }}>{bulkMsg}</span>
          )}
        </div>
      )}

      {viewMode === 'list' && (
        <div style={{ background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-warm-bg)' }}>
                <th style={{ padding: '12px 16px', width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={filteredContacts.length > 0 && filteredContacts.every((c) => selectedIds.has(c.id))}
                    ref={(el) => {
                      if (!el) return;
                      const selectedInFilter = filteredContacts.filter((c) => selectedIds.has(c.id)).length;
                      el.indeterminate = selectedInFilter > 0 && selectedInFilter < filteredContacts.length;
                    }}
                    onChange={toggleAll}
                  />
                </th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Name</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Company</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Stage</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Category</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Outreach</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Job Title</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Status</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Last contacted</th>
                <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Next follow-up</th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.map((c) => {
                const cat = c.category_id ? categoryMap[c.category_id] : null;
                return (
                  <tr
                    key={c.id}
                    style={{ borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}
                    onClick={() => navigate(`/crm/contact/${c.id}`)}
                  >
                    <td style={{ padding: '12px 16px' }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(c.id)}
                        onChange={() => toggleOne(c.id)}
                      />
                    </td>
                    <td style={{ padding: '12px 16px' }}>{c.first_name} {c.last_name}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>{c.company ?? '—'}</td>
                    <td style={{ padding: '12px 16px' }} onClick={(e) => e.stopPropagation()}>
                      <select
                        value={c.stage_id}
                        onChange={(e) => handleInlineStageChange(c, e.target.value)}
                        style={{ padding: '4px 8px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', fontSize: '13px', background: 'white' }}
                      >
                        {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '12px 16px' }}>{cat?.name ?? '—'}</td>
                    <td style={{ padding: '12px 16px' }}>{c.outreach_type}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>{c.job_title ?? '—'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      {c.employment_status === 'changed'
                        ? <span style={{ color: 'var(--color-coral)', fontWeight: 600 }}>Changed</span>
                        : c.employment_status === 'active' ? 'Active' : '—'}
                    </td>
                    <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>
                      {c.last_contacted_at ? formatDateDDMMYYYY(c.last_contacted_at) : '—'}
                    </td>
                    <td
                      style={{
                        padding: '12px 16px',
                        color: c.next_follow_up_date && new Date(c.next_follow_up_date) < new Date() ? 'var(--color-coral)' : 'var(--color-text-secondary)',
                      }}
                    >
                      {formatDateDDMMYYYY(c.next_follow_up_date) || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {viewMode === 'board' && (
        <div style={{ display: 'flex', gap: '8px', paddingBottom: '16px', flexWrap: 'nowrap', minWidth: 0 }}>
          {stages.map((stage) => {
            const stageContacts = byStage[stage.id] ?? [];
            const overdueCount = stageContacts.filter(isOverdue).length;
            return (
              <div
                key={stage.id}
                onDragOver={(e) => { e.preventDefault(); setDragOverStageId(stage.id); }}
                onDragLeave={() => setDragOverStageId(null)}
                onDrop={(e) => handleDrop(e, stage.id)}
                style={{
                  flex: '1 1 0',
                  minWidth: '100px',
                  background: 'white',
                  border: dragOverStageId === stage.id ? '1px solid var(--color-blue-dark)' : '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)',
                  padding: '8px',
                }}
              >
                <div
                  style={{
                    fontSize: '10px',
                    fontWeight: 600,
                    color: overdueCount > 0 ? 'var(--color-coral)' : 'var(--color-text-secondary)',
                    marginBottom: '6px',
                  }}
                >
                  {stage.name}
                  {stageContacts.length > 0 && (
                    <span style={{ marginLeft: '4px', fontWeight: 400 }}>
                      ({stageContacts.length}
                      {overdueCount > 0 && ` · ${overdueCount} overdue`})
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {stageContacts.map((contact) => {
                    const cat = contact.category_id ? categoryMap[contact.category_id] : null;
                    const overdue = isOverdue(contact);
                    const inactive = isInactive(contact);
                    return (
                      <div
                        key={contact.id}
                        role="button"
                        tabIndex={0}
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('contactId', contact.id)}
                        onClick={() => navigate(`/crm/contact/${contact.id}`)}
                        onKeyDown={(e) => e.key === 'Enter' && navigate(`/crm/contact/${contact.id}`)}
                        style={{
                          padding: '6px 8px',
                          background: 'white',
                          border: '1px solid var(--color-border)',
                          borderLeft: overdue ? '3px solid var(--color-coral)' : undefined,
                          borderRadius: 'var(--radius)',
                          cursor: 'grab',
                          opacity: inactive ? 0.85 : 1,
                        }}
                      >
                        <div style={{ fontWeight: 600, marginBottom: '2px', fontSize: '11px' }}>
                          {contact.first_name} {contact.last_name}
                        </div>
                        {contact.job_title && (
                          <div style={{ fontSize: '9px', color: 'var(--color-text-muted)', marginBottom: '1px' }}>{contact.job_title}</div>
                        )}
                        {contact.employment_status === 'changed' && (
                          <span style={{
                            display: 'inline-block',
                            fontSize: '8px',
                            fontWeight: 600,
                            background: 'var(--color-coral)',
                            color: 'white',
                            borderRadius: '3px',
                            padding: '1px 4px',
                            marginBottom: '2px',
                          }}>Changed</span>
                        )}
                        <div style={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>
                          {contact.company && <span>{contact.company}</span>}
                          {contact.company && ' · '}
                          {contact.outreach_type}
                          {cat && (
                            <span style={{ marginLeft: '2px' }}>
                              <span
                                style={{
                                  display: 'inline-block',
                                  width: '5px',
                                  height: '5px',
                                  borderRadius: '50%',
                                  background: cat.color || 'var(--color-blue-dark)',
                                  verticalAlign: 'middle',
                                  marginRight: '2px',
                                }}
                              />
                              {cat.name}
                            </span>
                          )}
                        </div>
                        {contact.last_contacted_at && (
                          <div style={{ fontSize: '9px', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                            Last: {formatDateDDMMYYYY(contact.last_contacted_at)}
                          </div>
                        )}
                        {overdue && <div style={{ fontSize: '9px', color: 'var(--color-coral)', marginTop: '2px' }}>Overdue</div>}
                        {inactive && !overdue && <div style={{ fontSize: '9px', color: 'var(--color-text-muted)', marginTop: '2px' }}>Inactive</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {viewMode === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Stats</h2>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 160px', minWidth: '160px', background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: '16px' }}>
                <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Total Active</div>
                <div style={{ fontSize: '28px', fontWeight: 700 }}>{totalActive}</div>
              </div>
              <div style={{ flex: '1 1 160px', minWidth: '160px', background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: '16px' }}>
                <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Due Today</div>
                <div style={{ fontSize: '28px', fontWeight: 700 }}>{dueToday}</div>
              </div>
              <div style={{ flex: '1 1 160px', minWidth: '160px', background: 'white', border: `1px solid ${overdueCount > 0 ? 'var(--color-coral)' : 'var(--color-border)'}`, borderRadius: 'var(--radius)', padding: '16px' }}>
                <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Overdue</div>
                <div style={{ fontSize: '28px', fontWeight: 700, color: overdueCount > 0 ? 'var(--color-coral)' : undefined }}>{overdueCount}</div>
              </div>
              <div style={{ flex: '1 1 160px', minWidth: '160px', background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: '16px' }}>
                <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>In Proposal</div>
                <div style={{ fontSize: '28px', fontWeight: 700 }}>{inProposal}</div>
              </div>
            </div>
          </div>

          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Pipeline</h2>
            <div style={{ background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {stageCounts.map(({ stage, count }) => (
                <div key={stage.id} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '140px', fontSize: '13px', color: 'var(--color-text-secondary)' }}>{stage.name}</div>
                  <div style={{ flex: 1, background: 'var(--color-warm-bg)', borderRadius: '4px', height: '20px', overflow: 'hidden' }}>
                    <div
                      onClick={() => count > 0 && handleBarClick(stage.id)}
                      style={{
                        width: `${(count / maxStageCount) * 100}%`,
                        height: '100%',
                        background: stage.is_terminal ? 'var(--color-text-muted)' : 'var(--color-blue-dark)',
                        borderRadius: '4px',
                        cursor: count > 0 ? 'pointer' : 'default',
                        transition: 'width 0.2s',
                      }}
                    />
                  </div>
                  <div style={{ width: '40px', textAlign: 'right', fontSize: '13px', fontWeight: 600 }}>{count}</div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Action Required</h2>
            {actionRequired.length === 0 ? (
              <div style={{ background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: '16px', color: 'var(--color-text-muted)', fontSize: '14px' }}>
                No overdue follow-ups.
              </div>
            ) : (
              <div style={{ background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-warm-bg)' }}>
                      <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Company</th>
                      <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Stage</th>
                      <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Follow-up</th>
                      <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Days overdue</th>
                      <th style={{ padding: '12px 16px' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {actionRequired.map((c) => {
                      const stage = stageById.get(c.stage_id);
                      const daysOverdue = Math.floor((Date.now() - new Date(c.next_follow_up_date as string).getTime()) / 86400000);
                      return (
                        <tr
                          key={c.id}
                          style={{ borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}
                          onClick={() => navigate(`/crm/contact/${c.id}`)}
                        >
                          <td style={{ padding: '12px 16px' }}>{c.first_name} {c.last_name}</td>
                          <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>{c.company ?? '—'}</td>
                          <td style={{ padding: '12px 16px' }}>{stage?.name ?? '—'}</td>
                          <td style={{ padding: '12px 16px', color: 'var(--color-coral)' }}>{formatDateDDMMYYYY(c.next_follow_up_date)}</td>
                          <td style={{ padding: '12px 16px' }}>{daysOverdue}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => { setLogContactId(c.id); setLogType('CALL'); setLogNotes(''); setLogStep2(false); setLogFollowUp(''); }}
                              className="btn btn-primary btn-sm"
                            >
                              Log contact
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Hot Leads</h2>
            {hotLeads.length === 0 ? (
              <div style={{ background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: '16px', color: 'var(--color-text-muted)', fontSize: '14px' }}>
                No contacts in Engaged or Proposal Sent.
              </div>
            ) : (
              <div style={{ background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-warm-bg)' }}>
                      <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Company</th>
                      <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Job Title</th>
                      <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Stage</th>
                      <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Next follow-up</th>
                      <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Last contacted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hotLeads.map((c) => {
                      const stage = stageById.get(c.stage_id);
                      return (
                        <tr
                          key={c.id}
                          style={{ borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}
                          onClick={() => navigate(`/crm/contact/${c.id}`)}
                        >
                          <td style={{ padding: '12px 16px' }}>{c.first_name} {c.last_name}</td>
                          <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>{c.company ?? '—'}</td>
                          <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>{c.job_title ?? '—'}</td>
                          <td style={{ padding: '12px 16px' }}>{stage?.name ?? '—'}</td>
                          <td style={{ padding: '12px 16px', color: c.next_follow_up_date && c.next_follow_up_date < todayStr ? 'var(--color-coral)' : 'var(--color-text-secondary)' }}>
                            {formatDateDDMMYYYY(c.next_follow_up_date) || '—'}
                          </td>
                          <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>
                            {c.last_contacted_at ? formatDateDDMMYYYY(c.last_contacted_at) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 12px' }}>Consider Archiving</h2>
            {archiveCandidates.length === 0 ? (
              <div style={{ background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: '16px', color: 'var(--color-text-muted)', fontSize: '14px' }}>
                No inactive contacts to review.
              </div>
            ) : (
              <>
                {archiveSelectedIds.size > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <button
                      type="button"
                      onClick={() => setArchiveConfirmOpen(true)}
                      style={{ padding: '8px 16px', background: 'var(--color-coral)', color: 'white', border: 'none', borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer' }}
                    >
                      Archive {archiveSelectedIds.size} selected
                    </button>
                  </div>
                )}
                <div style={{ background: 'white', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-warm-bg)' }}>
                        <th style={{ padding: '12px 16px', width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={archiveCandidates.length > 0 && archiveCandidates.every((c) => archiveSelectedIds.has(c.id))}
                            ref={(el) => {
                              if (!el) return;
                              const sel = archiveCandidates.filter((c) => archiveSelectedIds.has(c.id)).length;
                              el.indeterminate = sel > 0 && sel < archiveCandidates.length;
                            }}
                            onChange={toggleArchiveAll}
                          />
                        </th>
                        <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Name</th>
                        <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Company</th>
                        <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Stage</th>
                        <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Last contacted</th>
                        <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>Days inactive</th>
                      </tr>
                    </thead>
                    <tbody>
                      {archiveCandidates.map((c) => {
                        const stage = stageById.get(c.stage_id);
                        const daysInactive = c.last_contacted_at
                          ? Math.floor((Date.now() - new Date(c.last_contacted_at).getTime()) / 86400000)
                          : null;
                        return (
                          <tr key={c.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                            <td style={{ padding: '12px 16px' }}>
                              <input
                                type="checkbox"
                                checked={archiveSelectedIds.has(c.id)}
                                onChange={() => toggleArchiveOne(c.id)}
                              />
                            </td>
                            <td style={{ padding: '12px 16px' }}>{c.first_name} {c.last_name}</td>
                            <td style={{ padding: '12px 16px', color: 'var(--color-text-secondary)' }}>{c.company ?? '—'}</td>
                            <td style={{ padding: '12px 16px' }}>{stage?.name ?? '—'}</td>
                            <td style={{ padding: '12px 16px', color: c.last_contacted_at ? 'var(--color-text-secondary)' : 'var(--color-text-muted)' }}>
                              {c.last_contacted_at ? formatDateDDMMYYYY(c.last_contacted_at) : 'Never'}
                            </td>
                            <td style={{ padding: '12px 16px' }}>{daysInactive ?? '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {bulkConfirmOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => !bulkApplying && setBulkConfirmOpen(false)}
          role="presentation"
        >
          <div
            style={{ background: 'white', padding: '24px', borderRadius: 'var(--radius)', minWidth: '400px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px' }}>
              Move {selectedIds.size} contacts to {stages.find((s) => s.id === bulkStageId)?.name}?
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" disabled={bulkApplying} onClick={applyBulk} className="btn btn-primary">
                {bulkApplying ? 'Applying…' : 'Confirm'}
              </button>
              <button type="button" disabled={bulkApplying} onClick={() => setBulkConfirmOpen(false)} style={{ padding: '10px 20px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', background: 'transparent' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {logContactId && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => !logSaving && closeLogModal()}
          role="presentation"
        >
          <div
            style={{ background: 'white', padding: '24px', borderRadius: 'var(--radius)', minWidth: '400px' }}
            onClick={(e) => e.stopPropagation()}
          >
            {!logStep2 ? (
              <>
                <h3 style={{ margin: '0 0 16px' }}>Log contact</h3>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Type</label>
                <select
                  value={logType}
                  onChange={(e) => setLogType(e.target.value as InteractionType)}
                  style={{ width: '100%', padding: '8px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '12px' }}
                >
                  <option value="CALL">Call</option>
                  <option value="EMAIL">Email</option>
                  <option value="MEETING">Meeting</option>
                  <option value="MESSAGE">Message</option>
                  <option value="OTHER">Other</option>
                </select>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Notes</label>
                <textarea
                  value={logNotes}
                  onChange={(e) => setLogNotes(e.target.value)}
                  placeholder="Notes…"
                  rows={3}
                  style={{ width: '100%', padding: '8px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '12px', resize: 'vertical' }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    disabled={logSaving}
                    onClick={saveLogContact}
                    className="btn btn-primary"
                  >
                    {logSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    disabled={logSaving}
                    onClick={closeLogModal}
                    style={{ padding: '10px 20px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', background: 'transparent' }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 style={{ margin: '0 0 16px' }}>Interaction saved! Set next follow-up date?</h3>
                <input
                  type="date"
                  value={logFollowUp}
                  onChange={(e) => setLogFollowUp(e.target.value)}
                  style={{ width: '100%', padding: '10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button
                    type="button"
                    disabled={logSaving || !logFollowUp}
                    onClick={setLogFollowUpDate}
                    className="btn btn-primary"
                  >
                    {logSaving ? 'Saving…' : 'Set date'}
                  </button>
                  <button
                    type="button"
                    disabled={logSaving}
                    onClick={skipLogFollowUp}
                    style={{ background: 'transparent', border: 'none', color: 'var(--color-blue-dark)', textDecoration: 'underline', cursor: 'pointer', fontSize: '13px' }}
                  >
                    Skip
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {archiveConfirmOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => !archiving && setArchiveConfirmOpen(false)}
          role="presentation"
        >
          <div
            style={{ background: 'white', padding: '24px', borderRadius: 'var(--radius)', minWidth: '400px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px' }}>Archive {archiveSelectedIds.size} contacts?</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                disabled={archiving}
                onClick={applyArchive}
                style={{ padding: '10px 20px', background: 'var(--color-coral)', color: 'white', border: 'none', borderRadius: 'var(--radius)', fontWeight: 600 }}
              >
                {archiving ? 'Archiving…' : 'Confirm'}
              </button>
              <button
                type="button"
                disabled={archiving}
                onClick={() => setArchiveConfirmOpen(false)}
                style={{ padding: '10px 20px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', background: 'transparent' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => !deleting && setDeleteConfirmOpen(false)}
          role="presentation"
        >
          <div
            style={{ background: 'white', padding: '24px', borderRadius: 'var(--radius)', minWidth: '400px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px' }}>Permanently delete {selectedIds.size} contacts? This cannot be undone.</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                disabled={deleting}
                onClick={applyDelete}
                style={{ padding: '10px 20px', background: 'var(--color-coral)', color: 'white', border: 'none', borderRadius: 'var(--radius)', fontWeight: 600 }}
              >
                {deleting ? 'Deleting…' : 'Confirm'}
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={() => setDeleteConfirmOpen(false)}
                style={{ padding: '10px 20px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', background: 'transparent' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {inlineStagePending && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setInlineStagePending(null)}
          role="presentation"
        >
          <div
            style={{ background: 'white', padding: '24px', borderRadius: 'var(--radius)', minWidth: '400px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px' }}>Next follow-up date required</h3>
            <input
              type="date"
              value={inlineStageFollowUp}
              onChange={(e) => setInlineStageFollowUp(e.target.value)}
              style={{ width: '100%', padding: '10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" onClick={confirmInlineStage} className="btn btn-primary">Confirm</button>
              <button type="button" onClick={() => setInlineStagePending(null)} style={{ padding: '10px 20px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', background: 'transparent' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {pendingDrop && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setPendingDrop(null)}
          role="presentation"
        >
          <div
            style={{ background: 'white', padding: '24px', borderRadius: 'var(--radius)', minWidth: '400px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px' }}>Next follow-up date required</h3>
            <input
              type="date"
              value={pendingDropFollowUp}
              onChange={(e) => setPendingDropFollowUp(e.target.value)}
              style={{ width: '100%', padding: '10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
            />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" onClick={handlePendingDropConfirm} className="btn btn-primary">Confirm</button>
              <button type="button" onClick={() => setPendingDrop(null)} style={{ padding: '10px 20px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', background: 'transparent' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {drawerOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 100, display: 'flex', justifyContent: 'flex-end' }}
          onClick={() => setDrawerOpen(false)}
          role="presentation"
        >
          <div
            style={{
              width: '520px',
              maxHeight: '100vh',
              background: 'white',
              boxShadow: '-4px 0 24px rgba(0,0,0,0.1)',
              padding: '24px',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 16px' }}>New contact</h2>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>First name *</label>
            <input
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '12px' }}
            />
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Last name *</label>
            <input
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '12px' }}
            />
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Company</label>
            <input
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '12px' }}
            />
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '12px' }}
            />
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Job Title</label>
            <input
              value={form.job_title}
              onChange={(e) => setForm({ ...form, job_title: e.target.value })}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '12px' }}
            />
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Gender</label>
            <select
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '12px' }}
            >
              <option value="">— optional —</option>
              <option value="M">M</option>
              <option value="F">F</option>
              <option value="O">O</option>
            </select>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Employment Status</label>
            <select
              value={form.employment_status}
              onChange={(e) => setForm({ ...form, employment_status: e.target.value })}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '12px' }}
            >
              <option value="">— optional —</option>
              <option value="active">Active</option>
              <option value="changed">Changed</option>
            </select>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Outreach type *</label>
            <select
              value={form.outreach_type}
              onChange={(e) => setForm({ ...form, outreach_type: e.target.value as typeof form.outreach_type })}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '12px' }}
            >
              <option value="SALES">Sales</option>
              <option value="PARTNERSHIP">Partnership</option>
              <option value="INVESTOR">Investor</option>
              <option value="OTHER">Other</option>
            </select>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Stage *</label>
            <select
              value={form.stage_id}
              onChange={(e) => {
                const stageId = e.target.value;
                const stage = stages.find((s) => s.id === stageId);
                const nextFollowUp = stage?.requires_follow_up ? (form.next_follow_up_date || new Date().toISOString().slice(0, 10)) : '';
                setForm({ ...form, stage_id: stageId, next_follow_up_date: nextFollowUp });
              }}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '12px' }}
            >
              {stages.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {stageRequiresFollowUp && (
              <>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Next follow-up date *</label>
                <input
                  type="date"
                  value={toDateOnly(form.next_follow_up_date)}
                  onChange={(e) => setForm({ ...form, next_follow_up_date: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '12px' }}
                />
              </>
            )}
            <div style={{ display: 'flex', gap: '8px', marginTop: stageRequiresFollowUp ? 0 : '12px' }}>
              <button type="button" onClick={handleCreateContact} className="btn btn-primary">Create</button>
              <button type="button" onClick={() => setDrawerOpen(false)} style={{ padding: '10px 20px', background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
